/**
 * Microphone effect settings (noise suppression, echo cancellation, auto gain): the pure
 * mapping between the preference store, getUserMedia constraints and live track settings,
 * plus the in-call switch path. Pure functions here are unit-tested without a browser; the
 * live-switch helper is structural so tests inject a double instead of the LiveKit SDK.
 */

import type { AudioCaptureOptions } from 'livekit-client';

import type { DevicePrefs } from './devicePrefs';

/** The three browser microphone processing switches the effects dialog exposes. */
export interface AudioEffectSettings {
  readonly echoCancellation: boolean;
  readonly noiseSuppression: boolean;
  readonly autoGainControl: boolean;
}

/** Browser defaults for the three switches; also the migration default for old documents. */
export const DEFAULT_AUDIO_EFFECTS: AudioEffectSettings = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

/** One dialog toggle: the effect key. */
export type AudioEffectKey = keyof AudioEffectSettings;

/** Reads the three switches from preferences (missing/invalid keys degrade to the defaults). */
export function audioEffectSettingsFromPrefs(prefs: DevicePrefs): AudioEffectSettings {
  return {
    echoCancellation: prefs.micEchoCancellation,
    noiseSuppression: prefs.micNoiseSuppression,
    autoGainControl: prefs.micAutoGainControl,
  };
}

/**
 * Completes the possibly-absent join-request switches with the browser defaults (all on).
 * Positional optionals on purpose: exactOptionalPropertyTypes forbids passing explicit
 * `undefined` into a Partial object.
 */
export function audioEffectSettingsWithDefaults(
  echoCancellation: boolean | undefined,
  noiseSuppression: boolean | undefined,
  autoGainControl: boolean | undefined,
): AudioEffectSettings {
  return {
    echoCancellation: echoCancellation ?? DEFAULT_AUDIO_EFFECTS.echoCancellation,
    noiseSuppression: noiseSuppression ?? DEFAULT_AUDIO_EFFECTS.noiseSuppression,
    autoGainControl: autoGainControl ?? DEFAULT_AUDIO_EFFECTS.autoGainControl,
  };
}

/** Builds a store patch for one dialog toggle (exhaustive over the three effect keys). */
export function prefsPatchForEffect(key: AudioEffectKey, value: boolean): Partial<DevicePrefs> {
  switch (key) {
    case 'echoCancellation':
      return { micEchoCancellation: value };
    case 'noiseSuppression':
      return { micNoiseSuppression: value };
    case 'autoGainControl':
      return { micAutoGainControl: value };
  }
}

/**
 * getUserMedia audio constraints for the preview path: the three switches stay explicit so
 * the acquired track always reflects the stored preference (Chrome's default is true for
 * all three, but explicit constraints keep every browser on the same behavior).
 */
export function audioConstraintsOf(settings: AudioEffectSettings): MediaTrackConstraints {
  return {
    echoCancellation: settings.echoCancellation,
    noiseSuppression: settings.noiseSuppression,
    autoGainControl: settings.autoGainControl,
  };
}

/** LiveKit capture options for the publish path (deviceId omitted when null: SDK default). */
export function microphoneCaptureOptions(
  deviceId: string | null,
  settings: AudioEffectSettings,
): AudioCaptureOptions {
  const options: AudioCaptureOptions = {
    echoCancellation: settings.echoCancellation,
    noiseSuppression: settings.noiseSuppression,
    autoGainControl: settings.autoGainControl,
  };
  return deviceId === null ? options : { ...options, deviceId };
}

/** Reads one switch out of a track-settings value: true/false, or null when absent/unknown. */
function flagOfSetting(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }
  // The spec allows {exact, ideal} objects in settings; browsers currently report plain
  // booleans, but the shape is narrowed defensively instead of assumed.
  if (typeof value === 'object' && value !== null && 'exact' in value) {
    const exact: unknown = value.exact;
    if (typeof exact === 'boolean') {
      return exact;
    }
  }
  return null;
}

/**
 * The effective processing state of a live microphone track, read from getSettings(); null
 * when there is no live track or the browser reports none of the three switches. This is the
 * value the dialog rows show ("what the track actually does"), as opposed to the stored
 * preference ("what we asked for").
 */
export function readAudioEffectSettings(
  track: MediaStreamTrack | null | undefined,
): AudioEffectSettings | null {
  if (track === undefined || track === null || track.readyState !== 'live') {
    return null;
  }
  let settings: MediaTrackSettings;
  try {
    settings = track.getSettings();
  } catch {
    return null;
  }
  const echoCancellation = flagOfSetting(settings.echoCancellation);
  const noiseSuppression = flagOfSetting(settings.noiseSuppression);
  const autoGainControl = flagOfSetting(settings.autoGainControl);
  if (echoCancellation === null && noiseSuppression === null && autoGainControl === null) {
    return null;
  }
  return {
    echoCancellation: echoCancellation ?? DEFAULT_AUDIO_EFFECTS.echoCancellation,
    noiseSuppression: noiseSuppression ?? DEFAULT_AUDIO_EFFECTS.noiseSuppression,
    autoGainControl: autoGainControl ?? DEFAULT_AUDIO_EFFECTS.autoGainControl,
  };
}

/**
 * Structural slice of livekit-client's LocalAudioTrack the switch path needs: the underlying
 * MediaStreamTrack for applyConstraints, and restartTrack (SDK-supported re-acquisition that
 * swaps the new capture into the active sender without an unpublish). Tests inject doubles.
 */
export interface MicEffectsTrackHandle {
  readonly mediaStreamTrack: MediaStreamTrack;
  restartTrack(options: AudioCaptureOptions): Promise<unknown>;
}

/**
 * Applies new effect switches to a published, live microphone track.
 *
 * Path decision (verified against livekit-client 2.22.3): applyConstraints is tried first —
 * Chrome reconfigures the audio processing on a live track with no gap and no server churn;
 * the track object and publication stay untouched. Where the browser rejects the constraints
 * (Firefox/Safari throw instead of applying), the SDK's restartTrack re-acquires the capture
 * with the full capture options and swaps the replacement into the sender — a short (~1 s)
 * capture gap, still no republish. A failure of BOTH paths propagates: the caller surfaces
 * the error toast and keeps the (still live) track applyConstraints left untouched.
 */
export async function applyMicEffectSettings(
  track: MicEffectsTrackHandle,
  settings: AudioEffectSettings,
  captureOptions: AudioCaptureOptions,
): Promise<'applied' | 'reacquired'> {
  const constraints = audioConstraintsOf(settings);
  if (track.mediaStreamTrack.readyState === 'live') {
    try {
      await track.mediaStreamTrack.applyConstraints(constraints);
      return 'applied';
    } catch {
      // Fall through to the re-acquisition path below.
    }
  }
  await track.restartTrack(captureOptions);
  return 'reacquired';
}

/**
 * Dev-only e2e seam: exposes the real acquired microphone track's getSettings() to Playwright
 * page evaluation (`window.__vcMicSettings()`). Stripped from production builds by the
 * import.meta.env.DEV gate; returns null whenever no live track is currently held.
 */
export function exposeMicSettingsForTests(track: MediaStreamTrack | null): void {
  if (import.meta.env.DEV !== true) {
    return;
  }
  window.__vcMicSettings = (): MediaTrackSettings | null => {
    if (track === null || track.readyState !== 'live') {
      return null;
    }
    try {
      return track.getSettings();
    } catch {
      return null;
    }
  };
}

declare global {
  interface Window {
    /** Dev/e2e only: live microphone track settings for page evaluation. */
    __vcMicSettings?: () => MediaTrackSettings | null;
  }
}
